/***
*
*   useBundlePackaging hooks
*   Fetch and manage bundle packaging data (plans, features, add-ons)
*
**********/

import { useState, useCallback } from 'react';
import { useAPI } from 'components/lib';
import Axios from 'axios';

export function useBundlePackaging() {
  const response = useAPI('/api/dashboard/bundle-packaging');

  return {
    data: response?.data || null,
    loading: response.loading,
    error: response.error
  };
}

export function useFeatures() {
  const response = useAPI('/api/key/features');

  return {
    data: response?.data || [],
    loading: response.loading,
    error: response.error
  };
}

export function usePlanMutations() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const createPlan = useCallback(async (planData) => {
    setLoading(true);
    setError(null);
    try {
      const response = await Axios.post('/api/dashboard/bundle-packaging/plans', planData);
      setLoading(false);
      return response.data;
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create plan');
      setLoading(false);
      throw err;
    }
  }, []);

  const updatePlan = useCallback(async (planId, planData) => {
    setLoading(true);
    setError(null);
    try {
      const response = await Axios.patch(`/api/dashboard/bundle-packaging/plans/${planId}`, planData);
      setLoading(false);
      return response.data;
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update plan');
      setLoading(false);
      throw err;
    }
  }, []);

  const deletePlan = useCallback(async (planId) => {
    setLoading(true);
    setError(null);
    try {
      const response = await Axios.delete(`/api/dashboard/bundle-packaging/plans/${planId}`);
      setLoading(false);
      return response.data;
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete plan');
      setLoading(false);
      throw err;
    }
  }, []);

  return { createPlan, updatePlan, deletePlan, loading, error };
}

export function useFeatureMutations() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const createFeature = useCallback(async (featureData) => {
    setLoading(true);
    setError(null);
    try {
      const response = await Axios.post('/api/key/features', featureData);
      setLoading(false);
      return response.data;
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create feature');
      setLoading(false);
      throw err;
    }
  }, []);

  const updateFeature = useCallback(async (featureKey, featureData) => {
    setLoading(true);
    setError(null);
    try {
      const response = await Axios.patch(`/api/key/features/${featureKey}`, featureData);
      setLoading(false);
      return response.data;
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update feature');
      setLoading(false);
      throw err;
    }
  }, []);

  const deleteFeature = useCallback(async (featureKey) => {
    setLoading(true);
    setError(null);
    try {
      const response = await Axios.delete(`/api/key/features/${featureKey}`);
      setLoading(false);
      return response.data;
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete feature');
      setLoading(false);
      throw err;
    }
  }, []);

  return { createFeature, updateFeature, deleteFeature, loading, error };
}

export function useAddOnMutations() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const createAddOn = useCallback(async (addOnData) => {
    setLoading(true);
    setError(null);
    try {
      const response = await Axios.post('/api/dashboard/bundle-packaging/addons', addOnData);
      setLoading(false);
      return response.data;
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create add-on');
      setLoading(false);
      throw err;
    }
  }, []);

  const updateAddOn = useCallback(async (addOnId, addOnData) => {
    setLoading(true);
    setError(null);
    try {
      const response = await Axios.patch(`/api/dashboard/bundle-packaging/addons/${addOnId}`, addOnData);
      setLoading(false);
      return response.data;
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update add-on');
      setLoading(false);
      throw err;
    }
  }, []);

  const deleteAddOn = useCallback(async (addOnId) => {
    setLoading(true);
    setError(null);
    try {
      const response = await Axios.delete(`/api/dashboard/bundle-packaging/addons/${addOnId}`);
      setLoading(false);
      return response.data;
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete add-on');
      setLoading(false);
      throw err;
    }
  }, []);

  return { createAddOn, updateAddOn, deleteAddOn, loading, error };
}

export function useSettingsMutations() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const updateTrialSettings = useCallback(async (settings) => {
    setLoading(true);
    setError(null);
    try {
      const response = await Axios.patch('/api/dashboard/bundle-packaging/settings/trial', settings);
      setLoading(false);
      return response.data;
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update trial settings');
      setLoading(false);
      throw err;
    }
  }, []);

  const updateUpgradeSettings = useCallback(async (settings) => {
    setLoading(true);
    setError(null);
    try {
      const response = await Axios.patch('/api/dashboard/bundle-packaging/settings/upgrade', settings);
      setLoading(false);
      return response.data;
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update upgrade settings');
      setLoading(false);
      throw err;
    }
  }, []);

  const updateDiscounts = useCallback(async (discounts) => {
    setLoading(true);
    setError(null);
    try {
      const response = await Axios.patch('/api/dashboard/bundle-packaging/settings/discounts', discounts);
      setLoading(false);
      return response.data;
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update discounts');
      setLoading(false);
      throw err;
    }
  }, []);

  return { updateTrialSettings, updateUpgradeSettings, updateDiscounts, loading, error };
}